import React, { FC, useCallback, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import {
  CloseIcon,
  CloudUploadIcon,
  InfoCircle,
  UploadedFileIcon,
  UploadIcon,
} from '../../../assets/icons/index';
import LoaderIcon from '../../../assets/icons/loader-icon';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { HttpException } from '../../../exceptions/index';
import { useLocale, useModalState, useReactForm, VaultHooks } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { Repository, VaultSelect } from '../../../types/vault.types';
import {
  fileWithStorageDisplayName,
  findFirstDuplicateInDrop,
  isFileNameConflictWithExisting,
  resolveStorageFileName,
} from '../../../utils/file-upload-duplicate-names.util';
import { handleFormInvalid } from '../../../utils/handle-form-invalid.util';
import {
  VAULT_MAX_FILE_SIZE,
  VAULT_MAX_FILES_PER_UPLOAD,
  VAULT_PARALLEL_UPLOADS,
  VAULT_UPLOAD_ACCEPT,
} from '../constants/vault-upload.const';
import { FormSchema, formSchema } from './modals.validation';
import { RenameModal } from './rename-modal';

export type AddFileModalProps = {
  companyId: string;
  data: Repository;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  isOpenRepository: boolean;
  selectedFolderId: string | undefined;
};

export const AddFileModal: FC<AddFileModalProps> = ({
  companyId,
  handleClose,
  invalidateQuery,
  isOpenModal,
  selectedFolderId,
  data,
  isOpenRepository,
}) => {
  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
  });

  const vaultStorageFileNames = useMemo(
    () => data?.files?.map((file) => resolveStorageFileName(file.name, file.link)) ?? [],
    [data?.files],
  );

  const { files } = useWatch<FormSchema>({ control });
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const [filesToRename, setFilesToRename] = useState<
    { link: string; name: string; oldName: string }[]
  >([]);

  const addFileToRename = useCallback((link: string, name: string, oldName: string) => {
    setFilesToRename((prev) => [...prev, { link, name, oldName }]);
  }, []);

  const removeFileToRename = useCallback((nameToRemove: string | undefined) => {
    setFilesToRename((prev) => prev.filter((file) => file.oldName !== nameToRemove));
  }, []);

  const isValid = files && (files.some(({ docLink }) => docLink) || files.length > 0);
  const uploadedFiles = files
    ? files.reduce((prev, curr) => (curr.docLink ? prev + 1 : prev), 0)
    : 0;
  const isReachedUploadMaximum = isOpenRepository
    ? false
    : uploadedFiles >= VAULT_MAX_FILES_PER_UPLOAD;
  const getStepTwoFilesData = useCallback(() => getValues('files') || [], [getValues]);
  const { presignUploads } = VaultHooks.usePresignVaultUploads();

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach(({ file, errors }) => {
          errors.forEach((e) => {
            if (e.code === 'file-invalid-type') {
              setError('files', {
                type: 'manual',
                message: `${messagesLocale['INVALID_FILE_TYPE'].replace('{fileType}', file.name)}`,
              });
            }
            if (e.code === 'file-too-large') {
              setError('files', {
                type: 'manual',
                message: messagesLocale['FILE_SIZE_TOO_LARGE'],
              });
            }
          });
        });
        return;
      }

      if (isReachedUploadMaximum) return;
      const existingFiles = getStepTwoFilesData();
      const duplicateFile = findFirstDuplicateInDrop(
        acceptedFiles,
        existingFiles.map((file) => file.doc.name),
      );

      if (duplicateFile) {
        setError('files', {
          type: 'manual',
          message: messagesLocale['FILE_EXISTS'],
        });
        return;
      }

      const vaultDuplicateFile = findFirstDuplicateInDrop(acceptedFiles, vaultStorageFileNames);
      if (vaultDuplicateFile) {
        setError('files', {
          type: 'manual',
          message: messagesLocale['FILE_EXISTS'],
        });
        return;
      }

      const filesToUpload = isOpenRepository
        ? acceptedFiles
        : acceptedFiles.slice(0, VAULT_MAX_FILES_PER_UPLOAD - existingFiles.length);

      const entriesToUpload = filesToUpload
        .map((file) => {
          const { callback, abort } = ApiService.uploadFileToPresignedUrl();
          return { abort, callback, file, id: uuidv4() };
        })
        .filter(({ abort, file, id }) => {
          const { error, success } = formSchema.safeParse({
            files: [{ doc: file, docLink: '', loadProgress: 1, abort, id }],
          });
          if (!success) {
            setError('files', error.issues[0]);
            return false;
          }
          return true;
        });

      if (entriesToUpload.length === 0) return;

      clearErrors('files');

      setValue(
        'files',
        [
          ...(getValues('files') || []),
          ...entriesToUpload.map(({ abort, file, id }) => ({
            doc: file,
            docLink: '',
            loadProgress: 1,
            abort,
            id,
          })),
        ],
        { shouldValidate: true },
      );

      // One presign round-trip for the whole drop; bytes then go straight to S3.
      let presigned: VaultHooks.PresignedUpload[];
      try {
        presigned = await presignUploads(
          entriesToUpload.map(({ file }) => ({
            name: file.name,
            size: file.size,
            type: file.type,
          })),
        );
      } catch (e) {
        const droppedIds = new Set(entriesToUpload.map(({ id }) => id));
        setValue(
          'files',
          (getValues('files') || []).filter((f) => !droppedIds.has(f.id)),
        );
        toast.error(
          e instanceof HttpException ? e.message : messagesLocale['UNEXPECTED_ERROR_OCCURRED'],
        );
        return;
      }

      const uploadEntry = async (
        { callback, file, id }: (typeof entriesToUpload)[number],
        target: VaultHooks.PresignedUpload,
      ) => {
        try {
          await callback(target.uploadUrl, file, {
            onUploadProgress: (n) => {
              const currentFiles = getValues('files') || [];
              setValue(
                'files',
                currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: n } : f)),
              );
            },
            onAboard: () => {
              const currentFiles = getValues('files') || [];
              setValue(
                'files',
                currentFiles.filter((f) => f.id !== id),
              );
            },
          });

          const currentFilesAfterUpload = getValues('files') || [];
          setValue(
            'files',
            currentFilesAfterUpload.map((f) =>
              f.id === id
                ? {
                    ...f,
                    loadProgress: 100,
                    docLink: target.link,
                    doc: fileWithStorageDisplayName(file, target.link),
                  }
                : f,
            ),
          );
        } catch (e) {
          const currentFilesOnError = getValues('files') || [];
          setValue(
            'files',
            currentFilesOnError.filter((f) => f.id !== id),
          );
          toast.error(
            e instanceof HttpException ? e.message : messagesLocale['UNEXPECTED_ERROR_OCCURRED'],
          );
        }
      };

      // Bounded pool: parallel enough to saturate the uplink, without opening one
      // connection per file on a large drop.
      let cursor = 0;
      const workers = Array.from(
        { length: Math.min(VAULT_PARALLEL_UPLOADS, entriesToUpload.length) },
        async () => {
          while (cursor < entriesToUpload.length) {
            const index = cursor;
            cursor += 1;
            await uploadEntry(entriesToUpload[index], presigned[index]);
          }
        },
      );
      await Promise.all(workers);
    },
    [
      clearErrors,
      getStepTwoFilesData,
      getValues,
      isOpenRepository,
      isReachedUploadMaximum,
      messagesLocale,
      presignUploads,
      setError,
      setValue,
      vaultStorageFileNames,
    ],
  );

  const isVaultFileNameConflict = useCallback(
    (doc?: { name?: string }, docLink?: string) =>
      isFileNameConflictWithExisting({
        doc,
        docLink,
        existingNames: vaultStorageFileNames,
        filesToRename,
      }),
    [filesToRename, vaultStorageFileNames],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: VAULT_MAX_FILE_SIZE,
    noClick: false,
    multiple: true,
    disabled: isReachedUploadMaximum,
    accept: VAULT_UPLOAD_ACCEPT,
  });

  const handleDeleteFile = useCallback(
    (id: string) => {
      const current = (getValues('files') || []).find((file) => file.id === id);
      removeFileToRename(current?.doc.name);

      if (current && !current.docLink) current.abort();

      const currentData = (getValues('files') || []).filter((file) => file.id !== id);
      setValue('files', currentData);
      if (currentData.length === 0) return clearErrors('files');
    },
    [clearErrors, getValues, removeFileToRename, setValue],
  );

  const handleCloseModal = useCallback(() => {
    reset();
    setFilesToRename([]);
    handleClose();
  }, [reset, handleClose]);

  const { create } = VaultHooks.useCreateFile();

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const updatedFiles =
        data?.files?.map((file) => {
          // Byte size travels with the registration so the backend can skip a per-file
          // S3 HEAD round-trip when persisting the batch.
          const size = file?.doc?.size;
          const fileToRename = filesToRename.find((fr) => fr.link === file.docLink);
          return fileToRename
            ? { link: fileToRename.link, name: fileToRename.name, size }
            : { link: file.docLink, name: file?.doc?.name, size };
        }) || [];
      create(
        {
          companyId,
          data: {
            files: updatedFiles,
            folderId: selectedFolderId || undefined,
          },
        },
        {
          onSuccess: () => {
            toast.success(
              data?.files?.length > 1
                ? messagesLocaleFn('FILES_SUCCESSFULLY_UPLOADED', {
                    count: data?.files?.length,
                  })
                : messagesLocaleFn('FILE_SUCCESSFULLY_UPLOADED'),
            );
            invalidateQuery();
            handleCloseModal();
          },
        },
      );
    },
    [
      companyId,
      create,
      filesToRename,
      handleCloseModal,
      invalidateQuery,
      messagesLocaleFn,
      selectedFolderId,
    ],
  );

  const {
    toggler: renameModalToggler,
    isOpen: isOpenRenameModal,
    handleOpenModal: handleOpenRenameModal,
    handleCloseModal: handleCloseRenameModal,
    handleSuccessModal: handleSuccessRenameModal,
    modalData: currentName,
  } = useModalState<string | undefined, unknown, string>({
    onSuccess: (name?: string) => {
      if (name && currentName) {
        const currentFile = files?.find((file) => file.doc?.name === currentName);

        if (currentFile) {
          addFileToRename(currentFile.docLink || '', name, currentName || '');
        }
      }
    },
  });

  const isNamesDuplicates = files?.some(({ doc, docLink }) =>
    isVaultFileNameConflict(doc, docLink),
  );

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        side="RIGHT"
      >
        <AlertDialogWrapper control={{ onOpenChange: renameModalToggler, open: isOpenRenameModal }}>
          <RenameModal
            currentName={currentName}
            onClose={handleCloseRenameModal}
            onSuccess={handleSuccessRenameModal}
            type={VaultSelect.FILE}
          />
        </AlertDialogWrapper>

        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-[550] text-gray-700">
              <AppFormattedMessage id="UPLOAD_FILES_DOCUMENTS" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>

          <div className="flex h-full flex-col gap-4 overflow-hidden">
            <div className="flex h-full flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
              <span className="text-xs font-[450] text-gray-500">
                <AppFormattedMessage id="DOCUMENTS_UPLOAD" />
              </span>
              <input
                {...getInputProps()}
                accept=".pdf, .doc, .docx, .xlsx, .xls, .csv, .png, .jpeg, .jpg"
                className="hidden"
                multiple
                type="file"
              />
              <label
                {...getRootProps()}
                className={twMerge(
                  'relative flex w-full items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
                  isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
                )}
              >
                {errors?.files ? (
                  <InfoCircle className="size-14" thinIconVariant />
                ) : (
                  <CloudUploadIcon />
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex">
                    {errors?.files ? (
                      <span className="text-sm font-[550] text-fireside-600">
                        {errors?.files.message?.toString()}
                      </span>
                    ) : (
                      <p className="text-sm font-medium text-gray-700">
                        <span className="underline">
                          <AppFormattedMessage id="CLICK_TO_UPLOAD" />{' '}
                        </span>
                        <AppFormattedMessage id="OR_DRAG_AND_DROP" />
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 text-label-sm font-[450] text-gray-500">
                    <span>
                      <AppFormattedMessage
                        id="SUPPORTED_FORMATS"
                        values={{
                          formats: 'doc/docx, xls/xlsx, csv, jpeg/jpg, png or pdf',
                        }}
                      />
                    </span>
                    <span>
                      <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
                    </span>
                  </div>
                  {errors?.files && (
                    <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
                      <UploadIcon />
                      <AppFormattedMessage id="UPLOAD_AGAIN" />
                    </span>
                  )}
                </div>
              </label>
              {(files?.length || 0) > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="pb-2 text-xs font-[450] text-gray-500">
                    {uploadedFiles > 1 ? (
                      <AppFormattedMessage
                        id="COUNT_FILES_UPLOADED"
                        values={{ count: uploadedFiles }}
                      />
                    ) : (
                      <AppFormattedMessage id="ONE_FILE_UPLOADED" />
                    )}
                  </span>
                  {files?.map(({ doc, docLink, loadProgress, id }, i) => (
                    <div className="flex flex-col gap-2">
                      <div
                        className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25"
                        key={`${docLink}_${id}`}
                      >
                        <div className="flex justify-between p-3" key={`${doc?.name}_${i}`}>
                          <div className="flex items-center gap-2 overflow-hidden">
                            {docLink ? (
                              isVaultFileNameConflict(doc, docLink) ? (
                                <InfoCircle className="size-4" />
                              ) : (
                                <UploadedFileIcon />
                              )
                            ) : (
                              <LoaderIcon className="size-4" />
                            )}
                            <span className="truncate text-xs font-[450] text-gray-700">
                              {filesToRename.find((fr) => fr.oldName === doc?.name)?.name ||
                                doc?.name}
                            </span>
                          </div>
                          <Button
                            className="w-fit p-1 hover:bg-gray-50"
                            onClick={() => handleDeleteFile(id || '')}
                            styleType="NONE"
                            type="button"
                          >
                            <CloseIcon className="size-2" />
                          </Button>
                        </div>
                        <div
                          className={twMerge(
                            'h-[2px] shrink-0 bg-brand-700 transition-all duration-700',
                            docLink && 'bg-transparent',
                          )}
                          style={{ width: (loadProgress || (docLink && 100)) + '%' }}
                        />
                      </div>
                      {isVaultFileNameConflict(doc, docLink) && (
                        <div className="flex items-center gap-2 pl-3">
                          <span className="text-xs font-[450] text-fireside-600">
                            <AppFormattedMessage id="FILE_EXISTS" />
                          </span>
                          <Button
                            className="w-fit p-0 text-xs font-[450] text-brand-700 underline"
                            onClick={() => handleOpenRenameModal(doc?.name)}
                            styleType="NONE"
                          >
                            <AppFormattedMessage id="RENAME_FILE" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex h-9 w-full justify-between gap-3 px-6">
              <Button
                className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
                onClick={handleCloseModal}
                styleType="NONE"
              >
                <AppFormattedMessage id="CANCEL" />
              </Button>
              <div className="flex gap-3">
                <Button
                  className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
                  disabled={!isValid || isNamesDuplicates}
                  onClick={() => handleSubmit(submitHandler, handleFormInvalid)()}
                  type="button"
                >
                  <AppFormattedMessage id="DONE" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
